import api from '@/api/api.ts';
import { PageLoader } from '@/components/PageLoader.tsx';
import { UploadIcon } from 'lucide-react';
import { Button } from 'primereact/button';
import { FileUpload, FileUploadHeaderTemplateOptions, ItemTemplateOptions } from 'primereact/fileupload';
import { FC, useRef, useState } from 'react';
import { toast } from 'react-toastify';

interface FileUploadTemplateProps {
  title: string;
  apiEndpoint: string;
  buttonText: string;
  successMessage: string;
  validTitle: string;
}

export const FileUploadTemplate: FC<FileUploadTemplateProps> = ({
  title,
  apiEndpoint,
  buttonText,
  successMessage,
  validTitle,
}) => {
  const fileUploadRef = useRef<FileUpload>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationMessage, setValidationMessage] = useState('');
  const [isFileValid, setIsFileValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const headerTemplate = (options: FileUploadHeaderTemplateOptions) => {
    const { chooseButton } = options;
    return <div className={'file-upload-add absolute bottom-2 left-[50%] translate-x-[-50%] z-10'}>{chooseButton}</div>;
  };

  const onTemplateRemove = (callback: Function) => {
    setSelectedFile(null);
    setValidationMessage('');
    callback();
  };

  const itemTemplate = (inFile: object, props: ItemTemplateOptions) => {
    const file = inFile as File;
    return (
      <div className="flex text-left items-center justify-center gap-4">
        <span>{file.name}</span>
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
        <span
          className={
            'text-red-500 w-6 h-6 cursor-pointer text-sm flex items-center justify-center rounded-full border border-red-500'
          }
          onClick={() => onTemplateRemove(props.onRemove)}
        >
          x
        </span>
      </div>
    );
  };

  const emptyTemplate = () => {
    return (
      <div className="mt-2 mb-7 w-full flex flex-col items-center justify-center gap-2">
        <UploadIcon size={40} color={'#bfc5d0'} />
        <span className={'text-[#bfc5d0] text-lg'}>Drop your file here</span>
      </div>
    );
  };

  const onTemplateSelect = (e: any) => {
    const files = e.files;
    setSelectedFile(files[0]);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select or drop a XLSX file.');
      return;
    }

    setIsLoading(true);
    const formData = new FormData();
    formData.append('xlsx_file', selectedFile);

    try {
      const response = await api.post(apiEndpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data?.status) {
        setIsFileValid(true);
        setValidationMessage(successMessage);
      }

      if (fileUploadRef.current) {
        fileUploadRef.current.clear();
      }
    } catch (error: any) {
      setIsFileValid(false);
      if (error.response?.data?.detail) {
        setValidationMessage(error.response.data.detail);
      }

      if (fileUploadRef.current) {
        fileUploadRef.current.clear();
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <h1 className={'text-2xl font-bold mb-8 text-white'}>{title}</h1>
      <div className={'flex items-start gap-4'}>
        <div className={'px-12 py-8 bg-white rounded-sm min-w-80 w-80'}>
          <div className={'file-upload-wrapper relative w-full'}>
            <FileUpload
              className={'w-full'}
              ref={fileUploadRef}
              name={'xlsx_file'}
              accept=".xlsx,.xls,.docx,.doc"
              maxFileSize={100000000}
              customUpload={true}
              multiple={false}
              onSelect={onTemplateSelect}
              headerTemplate={headerTemplate}
              itemTemplate={itemTemplate}
              emptyTemplate={emptyTemplate}
            />
          </div>
          <div className={'mt-5'}>
            <Button className={'btn-primary w-full'} style={{ borderRadius: '6px' }} onClick={handleUpload}>
              {buttonText}
            </Button>
          </div>
        </div>
        {validationMessage && (
          <div className={'p-8 bg-white rounded-sm w-full'}>
            <h2 className={`text-lg font-bold mb-4 pb-4 border-b ${isFileValid ? 'text-green-500' : 'text-red-500'}`}>
              {isFileValid ? `${validTitle} OK` : `${validTitle} ERROR!`}
            </h2>
            <p className={isFileValid ? 'text-green-500' : 'text-red-500'}>{validationMessage}</p>
          </div>
        )}
      </div>
      <PageLoader visible={isLoading} />
    </>
  );
};
